import { useEffect, useMemo } from 'react';

import {
  aiAgentsApi,
  IntegrationActionRun,
  IntegrationActionRunArrayItem,
  isIntegrationActionRunArrayItem,
} from 'apis/aiAgents';
import {
  CustomFieldAttribute,
  CustomFieldAttributeValue,
  CustomFieldSelectAttributeOption,
} from 'apis/customFields';
import { isCustomFieldValueEmpty } from 'utils/customFields';
import { useCustomFieldsVisibility } from 'utils/customFields/useCustomFieldsVisibility';
import { CustomField } from 'utils/customFields/useLazyCustomFields/useLazyCustomFields.types';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { pubSub } from 'utils/pubSub';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { convertTreeToFlatItems } from 'utils/tree';

type UseCustomAttributesSuggestionsOptions = {
  customFields: readonly CustomField<
    CustomFieldAttribute,
    CustomFieldAttributeValue
  >[];
  csid: number;
};

type UseCustomAttributesSuggestionsResult = {
  suggestions: Record<string, CustomAttributeOption[]>;
  isFetching: boolean;
  isError: boolean;
};

export type CustomAttributeOption = {
  label: string;
  value: string[];
};

type FlattenAttribute = {
  options: Record<string, CustomAttributeOption>;
};

function getFlattenOptions(
  options: readonly CustomFieldSelectAttributeOption[],
): Record<string, CustomAttributeOption> {
  return convertTreeToFlatItems(options, {
    childrenKey: 'options',
    transformItem: (option, parentOptions) => {
      const label = parentOptions
        .map((node) => node.label)
        .concat(option.label)
        .join(' / ');
      const value = parentOptions
        .map((node) => node.value)
        .concat(option.value);
      return {
        id: option.id,
        label,
        value,
      };
    },
  }).reduce(
    (acc, { id, ...option }) => ({
      ...acc,
      [id]: option,
    }),
    {} as Record<string, CustomAttributeOption>,
  );
}

function getSuggestionsPerCustomAttributes(
  rawSuggestions: readonly IntegrationActionRun[],
  flattenAttributes: Record<string, FlattenAttribute>,
): Record<string, CustomAttributeOption[]> {
  return rawSuggestions
    .filter(
      (suggestion): suggestion is IntegrationActionRunArrayItem =>
        isIntegrationActionRunArrayItem(suggestion) &&
        suggestion.value.length > 0 &&
        !!flattenAttributes[suggestion.name],
    )
    .map((suggestion) => {
      const attribute = flattenAttributes[suggestion.name];
      // For non-select attributes the label and value are the same, and the value has only string
      if (Object.keys(attribute.options).length === 0) {
        return {
          id: suggestion.name,
          suggestions: [
            {
              label: suggestion.value[0],
              value: [...suggestion.value],
            },
          ],
        };
      }
      const mappedSuggestions = suggestion.value
        .map((optionId) => attribute.options?.[optionId])
        .filter((option): option is CustomAttributeOption => !!option);
      return { id: suggestion.name, suggestions: mappedSuggestions };
    })
    .reduce(
      (acc, { id, suggestions }) =>
        suggestions.length > 0 ? { ...acc, [id]: suggestions } : acc,
      {} as Record<string, CustomAttributeOption[]>,
    );
}

export function useCustomAttributeSuggestions({
  customFields,
  csid,
}: UseCustomAttributesSuggestionsOptions): UseCustomAttributesSuggestionsResult {
  const flags = useFeatureFlag(['custom-attributes-suggestions']);
  /**
   * The custom attributes schema is always the same, no need to recalculate it
   * every time a value changes, just when there are different number of custom
   * fields
   */
  const customAttributes = useMemo(
    () => customFields.map((customField) => customField.attribute),
    [customFields.length],
  );
  const queue = useAppSelector(
    (state) => state.conversation.metaConversation.conversation.queue,
  );

  const visibility = useCustomFieldsVisibility(customFields, {
    queue: [queue?.id ?? ''],
  });

  const areAllVisibleCustomAttributesFilled = useMemo(
    () =>
      customFields.filter(({ attribute: { id }, value }) => {
        if (!visibility.data[id]) {
          return false;
        }

        return isCustomFieldValueEmpty(value);
      }).length === 0,
    [customFields, visibility],
  );

  const flattenAttributes = useMemo(
    () =>
      customAttributes
        .map((customAttribute) => {
          const options =
            customAttribute.type === 'select' ? customAttribute.options : [];
          return {
            id: customAttribute.id,
            options: getFlattenOptions(options),
          };
        })
        .reduce(
          (acc, { id, options }) => ({ ...acc, [id]: { options } }),
          {} as Record<string, FlattenAttribute>,
        ),
    [customAttributes],
  );

  const skipFetchingSuggestions =
    !flags.customAttributesSuggestions || areAllVisibleCustomAttributesFilled;

  const {
    isFetching,
    refetch,
    data: suggestions,
    isError,
  } = aiAgentsApi.useCreateToolsIntegrationsActionsRunQuery(
    {
      integrationType: 'dixa',
      actionId: 'suggest_conversation_custom_attributes',
      csid,
    },
    {
      skip: skipFetchingSuggestions,
      selectFromResult: ({ data, ...rest }) => ({
        data: getSuggestionsPerCustomAttributes(
          data?.items ?? [],
          flattenAttributes,
        ),
        ...rest,
      }),
    },
  );

  useEffect(() => {
    if (skipFetchingSuggestions) {
      return;
    }
    const unsubscribe = pubSub.subscribe(
      'conversation:message_added',
      (payload) => {
        if (payload.csid === csid) {
          refetch();
        }
      },
    );
    return unsubscribe;
  }, [csid, skipFetchingSuggestions, refetch]);

  return { suggestions, isFetching, isError };
}
